import { skipToken } from '@reduxjs/toolkit/dist/query';
import useServerSideQuery from 'hooks/useServerSideQuery';
import {
  useGetPurchaseRequestGroupsQuery,
  useGetPurchaseRequestsV2Query,
} from 'store/slices/clientV2/purchaseRequests';
import { ALL, DISABLE_PAGE_LIMIT, Lite } from 'types/api';
import {
  PurchaseRequestGroupSchema,
  PurchaseRequestSchema,
} from 'types/clientV2/purchaseRequests';

export type PurchaseRequestGroupDetailSchema = Lite<
  PurchaseRequestGroupSchema,
  'destinationLocation' | 'org' | 'requestedBy'
>;

export type PurchaseRequestCartSchema = Lite<
  PurchaseRequestSchema,
  'v2OrgPart' | 'v2OrgParts' | 'orgSupplier' | 'org' | 'createdBy'
>;

export const useGetPurchaseRequestGroupDetail = (
  purchaseRequestGroupId: string | undefined
) => {
  const {
    data: { data: [purchaseRequestGroup] } = { data: [null] },
    isLoading: isLoadingCart,
    isFetching: isFetchingCart,
    isError: isErrorCart,
  } = useServerSideQuery<
    PurchaseRequestGroupSchema,
    PurchaseRequestGroupDetailSchema
  >(
    useGetPurchaseRequestGroupsQuery,
    purchaseRequestGroupId
      ? {
          pageSize: 1,
          filters: [
            {
              field: 'id',
              operator: 'is',
              value: purchaseRequestGroupId,
            },
          ],
          schema: [
            ALL,
            {
              org: [],
              requestedBy: [],
              destinationLocation: [],
            },
          ],
          blended: true,
        }
      : skipToken
  );

  const {
    data: purchaseRequestsResponse,
    isLoading: isLoadingPurchaseRequests,
    isFetching: isFetchingPurchaseRequests,
    isError: isErrorPurchaseRequests,
  } = useServerSideQuery<PurchaseRequestSchema, PurchaseRequestCartSchema>(
    useGetPurchaseRequestsV2Query,
    purchaseRequestGroupId
      ? {
          pageSize: DISABLE_PAGE_LIMIT,
          filters: [
            {
              field: 'purchaseRequestGroup.id',
              operator: '=',
              value: purchaseRequestGroupId,
            },
          ],
          schema: [
            ALL,
            {
              v2OrgPart: [],
              v2OrgParts: [],
              orgSupplier: [],
              org: [],
              createdBy: [],
            },
          ],
        }
      : skipToken
  );

  const purchaseRequests = purchaseRequestsResponse?.data || [];

  return {
    purchaseRequestGroup,
    purchaseRequests,
    isLoading: isLoadingCart || isLoadingPurchaseRequests,
    isFetching: isFetchingCart || isFetchingPurchaseRequests,
    isError: isErrorCart || isErrorPurchaseRequests,
  };
};
